import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Paper } from '@material-ui/core';
import CustomHeader from 'components/CustomHeader/CustomHeader';
import Spinner from 'components/UI/Spinner/Spinner';
import {
    SHARING_AGREEMENT_STATUS,
    SHARING_AGREEMENT_STATUS_V2,
    SHARING_AGREEMENTS,
    UPDATE_AGREEMENTS_ERROR,
    UPDATE_AGREEMENTS_SUCCESS
} from 'constants/Constants';
import { setCurrentPage } from 'store/actions';
import { SHOW_DANGER_BAR, SHOW_SUCCESS_BAR } from 'store/actions/types';
import ConfirmDecisionDialog from './ConfirmDecisionDialog';
import StatusChip from './StatusChip';
import {
    SHARING_REQUEST_STATUS,
    SHARING_SOURCE,
    carrierNameFromRecord,
    fromV1Connection,
    fromV2Agreement,
    titleStatus,
    typeLabelForSource,
    withV2RespondedStatus
} from './sharingRequestModel';
import {
    decideV1Connection,
    decideV1Group,
    fetchV1Connection,
    fetchV1Groups,
    fetchV2Agreement,
    saveV2Agreement
} from './visibilitySharingApi';
import V1SharingGroups from './V1SharingGroups';
import V2SharingGroups from './V2SharingGroups';
import styles from './VisibilitySharing.module.css';

function sourceFromSearch(search) {
    const source = new URLSearchParams(search || '').get('source');
    if (source === SHARING_SOURCE.V2) {
        return SHARING_SOURCE.V2;
    }
    if (source === SHARING_SOURCE.V1) {
        return SHARING_SOURCE.V1;
    }
    return '';
}

function sameId(left, right) {
    if (left == null || right == null || left === '' || right === '') {
        return false;
    }
    return String(left) === String(right);
}

export const VisibilitySharingDetailBase = ({
    match,
    location,
    history,
    setCurrentPage,
    dispatch
}) => {
    const agreementId = decodeURIComponent(match?.params?.id || '');
    const source = sourceFromSearch(location?.search);
    const loadGenerationRef = useRef(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [connection, setConnection] = useState(null);
    const [groups, setGroups] = useState({});
    const [agreement, setAgreement] = useState(null);
    const [decision, setDecision] = useState(null);
    const [saving, setSaving] = useState(false);

    const clearDetailState = () => {
        setConnection(null);
        setGroups({});
        setAgreement(null);
        setDecision(null);
        setError('');
    };

    const load = () => {
        const requestedId = agreementId;
        const requestedSource = source;
        const generation = ++loadGenerationRef.current;

        clearDetailState();
        setLoading(true);

        const isCurrent = () => generation === loadGenerationRef.current;

        if (!requestedId || !requestedSource) {
            if (isCurrent()) {
                setError('This sharing agreement could not be opened.');
                setLoading(false);
            }
            return Promise.resolve();
        }

        if (requestedSource === SHARING_SOURCE.V1) {
            return Promise.all([fetchV1Connection(requestedId), fetchV1Groups(requestedId)])
                .then(([connectionRecord, groupRecord]) => {
                    if (!isCurrent()) {
                        return;
                    }
                    setConnection(connectionRecord || {});
                    setGroups(groupRecord && typeof groupRecord === 'object' ? groupRecord : {});
                })
                .catch(() => {
                    if (isCurrent()) {
                        setError('This sharing agreement could not be loaded.');
                    }
                })
                .finally(() => {
                    if (isCurrent()) {
                        setLoading(false);
                    }
                });
        }

        return fetchV2Agreement(requestedId)
            .then((record) => {
                if (!isCurrent()) {
                    return;
                }
                setAgreement(record || {});
            })
            .catch(() => {
                if (isCurrent()) {
                    setError('This sharing agreement could not be loaded.');
                }
            })
            .finally(() => {
                if (isCurrent()) {
                    setLoading(false);
                }
            });
    };

    useEffect(() => {
        setCurrentPage(SHARING_AGREEMENTS);
    }, [setCurrentPage]);

    useEffect(() => {
        load();
        return () => {
            loadGenerationRef.current += 1;
        };
    }, [agreementId, source]);

    const notify = (ok) => {
        dispatch({
            type: ok ? SHOW_SUCCESS_BAR : SHOW_DANGER_BAR,
            payload: ok ? UPDATE_AGREEMENTS_SUCCESS : UPDATE_AGREEMENTS_ERROR
        });
    };

    const goBack = () => {
        history.push({
            pathname: '/sharing-agreements',
            state: {
                statusFilter: location?.state?.statusFilter,
                searchValue: location?.state?.searchValue
            }
        });
    };

    const renderedRecordId =
        source === SHARING_SOURCE.V1 ? connection?.id : agreement?.id;
    const detailMatchesRoute = sameId(renderedRecordId, agreementId);

    const ensureDetailMatchesRoute = () => {
        if (detailMatchesRoute) {
            return true;
        }
        notify(false);
        return false;
    };

    const requestRow =
        source === SHARING_SOURCE.V1
            ? fromV1Connection(connection || {}, groups)
            : fromV2Agreement(agreement || {});
    const carrierName =
        (source === SHARING_SOURCE.V1
            ? carrierNameFromRecord(connection)
            : carrierNameFromRecord(agreement)) || '';
    const parentPending =
        source === SHARING_SOURCE.V1 &&
        titleStatus(connection?.request_status) === SHARING_REQUEST_STATUS.PENDING;

    const applyDecision = () => {
        if (!decision || !ensureDetailMatchesRoute() || saving) {
            setDecision(null);
            return;
        }
        const current = decision;
        setDecision(null);
        setSaving(true);
        let request;
        if (source === SHARING_SOURCE.V1 && !current.groupName) {
            request = decideV1Connection(
                renderedRecordId,
                current.decision === 'accept'
                    ? SHARING_AGREEMENT_STATUS.ACCEPTED
                    : SHARING_AGREEMENT_STATUS.REJECTED
            );
        } else if (source === SHARING_SOURCE.V1) {
            request = decideV1Group(
                renderedRecordId,
                current.groupName,
                current.decision === 'accept'
                    ? SHARING_AGREEMENT_STATUS.ACCEPTED
                    : SHARING_AGREEMENT_STATUS.REJECTED
            );
        } else {
            const nextStatus =
                current.decision === 'accept'
                    ? SHARING_AGREEMENT_STATUS_V2.ACCEPTED
                    : SHARING_AGREEMENT_STATUS_V2.REJECTED;
            const updatedAgreement = withV2RespondedStatus(
                agreement,
                nextStatus,
                current.allPending
                    ? { allPending: true }
                    : {
                          eventType: current.eventType,
                          groupIndex: current.groupIndex
                      }
            );
            if (!sameId(updatedAgreement?.id, agreementId)) {
                notify(false);
                setSaving(false);
                return;
            }
            request = saveV2Agreement(updatedAgreement).then((response) => {
                setAgreement(updatedAgreement);
                return response;
            });
        }
        request
            .then(() => {
                notify(true);
                return load();
            })
            .catch(() => notify(false))
            .finally(() => setSaving(false));
    };

    const acceptWholeRequest = () => {
        if (!ensureDetailMatchesRoute() || saving) {
            return;
        }
        setSaving(true);
        if (source === SHARING_SOURCE.V1) {
            decideV1Connection(renderedRecordId, SHARING_AGREEMENT_STATUS.ACCEPTED)
                .then(() => {
                    notify(true);
                    return load();
                })
                .catch(() => notify(false))
                .finally(() => setSaving(false));
            return;
        }
        const updatedAgreement = withV2RespondedStatus(agreement, SHARING_AGREEMENT_STATUS_V2.ACCEPTED, {
            allPending: true
        });
        if (!sameId(updatedAgreement?.id, agreementId)) {
            notify(false);
            setSaving(false);
            return;
        }
        saveV2Agreement(updatedAgreement)
            .then(() => {
                setAgreement(updatedAgreement);
                notify(true);
                return load();
            })
            .catch(() => notify(false))
            .finally(() => setSaving(false));
    };

    const onGroupDecide = (target, nextDecision) => {
        if (!ensureDetailMatchesRoute() || saving) {
            return;
        }
        if (nextDecision === 'decline') {
            setDecision({ decision: nextDecision, ...target });
            return;
        }
        setSaving(true);
        const request =
            source === SHARING_SOURCE.V1
                ? decideV1Group(
                      renderedRecordId,
                      target.groupName,
                      SHARING_AGREEMENT_STATUS.ACCEPTED
                  )
                : (() => {
                      const updatedAgreement = withV2RespondedStatus(
                          agreement,
                          SHARING_AGREEMENT_STATUS_V2.ACCEPTED,
                          {
                              eventType: target.eventType,
                              groupIndex: target.groupIndex
                          }
                      );
                      if (!sameId(updatedAgreement?.id, agreementId)) {
                          notify(false);
                          setSaving(false);
                          return Promise.reject(new Error('Detail no longer matches the route'));
                      }
                      return saveV2Agreement(updatedAgreement);
                  })();
        request
            .then(() => {
                notify(true);
                return load();
            })
            .catch(() => notify(false))
            .finally(() => setSaving(false));
    };

    const showDetail = !loading && !error && detailMatchesRoute;

    return (
        <>
            <CustomHeader title="SHARING AGREEMENTS" />
            <Paper>
                <div className={styles.headerBlock}>
                    <button type="button" className={styles.back} onClick={goBack}>
                        Back to sharing agreements
                    </button>
                </div>
                {loading ? (
                    <Spinner />
                ) : error ? (
                    <div className={styles.empty} role="alert">
                        {error}
                    </div>
                ) : !detailMatchesRoute ? (
                    <Spinner />
                ) : (
                    <div className={styles.section}>
                        <div className={styles.summary}>
                            <div className={styles.summaryMain}>
                                <h1 className={styles.carrierName}>{carrierName}</h1>
                                <div className={styles.summaryMeta}>
                                    <StatusChip
                                        status={requestRow.status}
                                        pendingGroupCount={requestRow.pendingGroupCount}
                                    />
                                    <div>
                                        <span className={styles.termLabel}>Type</span>
                                        <div className={styles.termValue}>
                                            {typeLabelForSource(source) || requestRow.typeLabel}
                                        </div>
                                    </div>
                                    {requestRow.direction ? (
                                        <div>
                                            <span className={styles.termLabel}>Direction</span>
                                            <div className={styles.termValue}>
                                                {requestRow.direction}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                                {parentPending ? (
                                    <p className={styles.helper}>
                                        Accept this request to respond to each group.
                                    </p>
                                ) : null}
                            </div>
                            {showDetail && requestRow.canDecideWholeRequest ? (
                                <div className={styles.summaryActions}>
                                    <button
                                        type="button"
                                        className={styles.acceptButton}
                                        onClick={acceptWholeRequest}
                                    >
                                        {source === SHARING_SOURCE.V2
                                            ? 'Accept all pending'
                                            : 'Accept request'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.declineButton}
                                        onClick={() =>
                                            setDecision(
                                                source === SHARING_SOURCE.V2
                                                    ? { decision: 'decline', allPending: true }
                                                    : { decision: 'decline' }
                                            )
                                        }
                                    >
                                        {source === SHARING_SOURCE.V2
                                            ? 'Decline all pending'
                                            : 'Decline request'}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                        {source === SHARING_SOURCE.V1 ? (
                            <section className={styles.sharedSection}>
                                <h2 className={styles.sharedHeading}>What is being shared</h2>
                                <V1SharingGroups
                                    groups={groups}
                                    parentPending={parentPending}
                                    onDecide={onGroupDecide}
                                />
                            </section>
                        ) : (
                            <V2SharingGroups agreement={agreement} onDecide={onGroupDecide} />
                        )}
                    </div>
                )}
            </Paper>
            <ConfirmDecisionDialog
                open={Boolean(decision) && detailMatchesRoute}
                decision={decision?.decision}
                carrierName={carrierName}
                groupName={decision?.groupName}
                scope={
                    decision?.allPending
                        ? 'pending'
                        : decision?.groupIndex != null || decision?.groupName
                        ? 'group'
                        : 'request'
                }
                onCancel={() => setDecision(null)}
                onConfirm={applyDecision}
            />
        </>
    );
};

export default withRouter(
    connect(null, (dispatch) => ({
        dispatch,
        setCurrentPage: (page) => dispatch(setCurrentPage(page))
    }))(VisibilitySharingDetailBase)
);
